import { SyntheticEvent, useEffect, useState } from "react";
import { Box, Alert, Card, CardContent } from "@mui/material";
import useCleaningData from "./use-cleaning-data";
import { AutocompleteModel, ClientCleaningModel } from "./types";
import CleaningHeader from "./cleaning-header";
import ClientTabs from "./client-tabs";
import ChartSection from "./chart-section";
import NoCleaningData from "./no-cleaning-data";
import LoadingScreen from "../progress/loading-screen";

const SEARCH_RESULTS_LOADING_MESSAGES = ["Loading cleaning data.."];

const CleaningData = () => {
  const { data, isLoading, error, mutate } = useCleaningData();

  const [view, setView] = useState<"pie" | "bar">("pie");
  const [selectedClient, setSelectedClient] = useState<
    ClientCleaningModel | undefined
  >(data?.[0]);
  const [searchValue, setSearchValue] = useState<AutocompleteModel | undefined>(
    undefined,
  );

  useEffect(() => {
    if (!selectedClient) setSelectedClient(data?.[0]);
  }, [data, selectedClient]);

  if (error) {
    return <Alert severity="error">Unable to load cleaning data.</Alert>;
  }

  const handleSwitchChange = () =>
    setView((prev) => (prev === "pie" ? "bar" : "pie"));

  const handleAutocompleteChange = (
    event: SyntheticEvent,
    newValue: string | AutocompleteModel | null,
  ) => {
    const isModel = newValue && typeof newValue !== "string";
    setSearchValue(isModel ? newValue : undefined);
    setSelectedClient(
      isModel && data && newValue?.count >= 0
        ? data[newValue.count]
        : data?.[0],
    );
  };

  const handleTabChange = (
    event: SyntheticEvent,
    newValue: string | number,
  ) => {
    const client = data?.find((c) => c.clientName === newValue);
    setSelectedClient(client);
  };

  if (isLoading) {
    return (
      <LoadingScreen
        height="200px"
        dataTestId="cleaning-data-loading"
        loadingMessages={SEARCH_RESULTS_LOADING_MESSAGES}
      />
    );
  }

  return (
    <Card
      variant="outlined"
      sx={{ width: "100%", p: 2 }}
      data-testid="cleaning-data-card"
    >
      <CardContent>
        <CleaningHeader
          data={data}
          searchValue={searchValue}
          onAutocompleteChange={handleAutocompleteChange}
        />

        <NoCleaningData isLoading={isLoading} dataLength={data?.length} />

        {data && data?.length > 0 && (
          <Box
            sx={{
              backgroundColor: "background.paper",
              borderRadius: 1,
            }}
          >
            <ClientTabs
              data={data}
              selectedClient={selectedClient}
              onTabChange={handleTabChange}
              view={view}
              onSwitchChange={handleSwitchChange}
            />

            <ChartSection
              selectedClient={selectedClient}
              view={view}
              mutate={mutate}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default CleaningData;
